import { useEffect, useState } from 'react';
import {
  fetchRemoteExpensesSummary,
  fetchRemoteProfitLossSummary,
  sumExpensesForBranchPeriod,
} from '../../../expenses/infra/expensesRepo';
import type { ExpensesSummary, ProfitLossSummary } from '../../../expenses/domain/expenseTypes';
import { isGlobalFeatureOn } from '../../../shell/feature-flags/globalFeatureFlagsService';
import { useGlobalFeatureFlags } from '../../../shell/providers/GlobalFeatureFlagsProvider';
import {
  getReportsDateRange,
} from '../reportsScreen.utils';
import type { DateRangePreset } from '../reportsScreen.constants';
import type { CashSummary } from '../../../cash/domain-api';

type Params = {
  currentBranchId: string | null | undefined;
  dateRangePreset: DateRangePreset;
  customRangeFrom: Date;
  customRangeTo: Date;
  cashSessionOutflowsTotal: number | null | undefined;
};

export function useReportsExpensesPane({
  currentBranchId,
  dateRangePreset,
  customRangeFrom,
  customRangeTo,
  cashSessionOutflowsTotal,
}: Params) {
  const [expensesSummary, setExpensesSummary] = useState<ExpensesSummary | null>(null);
  const [profitLoss, setProfitLoss] = useState<ProfitLossSummary | null>(null);
  const [expensesProfitLossLoading, setExpensesProfitLossLoading] = useState(false);

  const { flags: globalFeatureFlags } = useGlobalFeatureFlags();
  const expensesModuleEnabled = isGlobalFeatureOn(globalFeatureFlags, 'expenses_module');

  useEffect(() => {
    if (!expensesModuleEnabled || !currentBranchId) {
      setExpensesSummary(null);
      setProfitLoss(null);
      return;
    }
    let cancelled = false;
    setExpensesProfitLossLoading(true);
    void (async () => {
      const range = getReportsDateRange(
        new Date(),
        dateRangePreset,
        dateRangePreset === 'custom' ? { from: customRangeFrom, to: customRangeTo } : null
      );
      const fromIso = range.fromIso;
      const toIso = range.toIso;
      const fromDate = fromIso.slice(0, 10);
      const toDate = toIso.slice(0, 10);
      const remote = await fetchRemoteExpensesSummary(currentBranchId, fromIso, toIso);
      const localTotal = await sumExpensesForBranchPeriod({
        branchId: currentBranchId,
        fromDate,
        toDate,
      });
      const pl = await fetchRemoteProfitLossSummary(currentBranchId, fromIso, toIso);
      if (cancelled) return;
      setExpensesSummary(
        remote ?? {
          expensesTotal: localTotal,
          cashOutflowsTotal: cashSessionOutflowsTotal ?? 0,
          unpaidTotal: 0,
          byCategory: [],
          bySettlementMethod: [],
          byPaymentStatus: [],
          linkedToCashSessionTotal: cashSessionOutflowsTotal ?? 0,
          unlinkedToCashSessionTotal: Math.max(0, localTotal - (cashSessionOutflowsTotal ?? 0)),
        }
      );
      setProfitLoss(pl);
      if (!cancelled) setExpensesProfitLossLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [expensesModuleEnabled, currentBranchId, dateRangePreset, customRangeFrom, customRangeTo, cashSessionOutflowsTotal]);

  return {
    expensesSummary,
    profitLoss,
    expensesProfitLossLoading,
    expensesModuleEnabled,
  };
}
